/**
 * Zustand store cho phòng RPS p2p (member tự tạo) — tách riêng khỏi rpsStore (tournament).
 */
import { create } from 'zustand';

export type RPSChoice = 'rock' | 'paper' | 'scissors';
export type RPSRoomStatus = 'waiting' | 'playing' | 'ended';
export type RPSRoomRole = 'player' | 'spectator';

export type RPSRoomPlayerInfo = {
  employee_id: string;
  display_name: string | null;
  avatar_url: string | null;
};

export type RPSRoomReveal = {
  game_number: number;
  player1_choice: RPSChoice;
  player2_choice: RPSChoice;
  result: 'player1_wins' | 'player2_wins' | 'draw';
  is_random_tiebreak: boolean;
};

export type RPSRoomChatItem = {
  id: string;
  sender_id: string;
  sender_name: string | null;
  sender_avatar: string | null;
  is_player: boolean;
  message: string;
  sent_at: number;
};

export type RPSRoomMatchEnd = {
  winner_id: string;
  player1_games_won: number;
  player2_games_won: number;
  games_played: number;
  reason: 'completed' | 'opponent_left';
};

type RPSRoomState = {
  room_id: string | null;
  name: string;
  status: RPSRoomStatus;
  role: RPSRoomRole;
  myEmployeeId: string | null;
  maxGames: number;
  timeLimitSec: number;
  player1: RPSRoomPlayerInfo | null;
  player2: RPSRoomPlayerInfo | null;
  spectatorCount: number;
  readyIds: string[];
  matchStarted: boolean;
  winsNeeded: number;

  gameNumber: number;
  gameStartedAt: number | null;
  player1GamesWon: number;
  player2GamesWon: number;
  chosenIds: string[];
  myChoiceSubmitted: boolean;
  lastReveal: RPSRoomReveal | null;
  revealHistory: RPSRoomReveal[];

  matchEnd: RPSRoomMatchEnd | null;
  closeReason: 'completed' | 'opponent_left' | 'closed_by_owner' | null;

  chat: RPSRoomChatItem[];

  // actions
  setJoined: (data: {
    room_id: string;
    name: string;
    status: RPSRoomStatus;
    role: RPSRoomRole;
    my_employee_id: string;
    max_games: number;
    time_limit_sec: number;
    player1: RPSRoomPlayerInfo | null;
    player2: RPSRoomPlayerInfo | null;
    ready_ids: string[];
    spectator_count: number;
  }) => void;
  setStateUpdated: (data: { player1: RPSRoomPlayerInfo | null; player2: RPSRoomPlayerInfo | null; spectator_count: number }) => void;
  setReadyUpdated: (data: { ready_ids: string[] }) => void;
  setMatchStarted: (data: { max_games: number; time_limit_sec: number; wins_needed: number }) => void;
  setGameStarted: (data: { game_number: number; started_at: number; timer_sec: number; player1_games_won: number; player2_games_won: number }) => void;
  setPlayerChosen: (data: { employee_id: string }) => void;
  setGameRevealed: (data: RPSRoomReveal) => void;
  setMatchEnded: (data: RPSRoomMatchEnd) => void;
  setRoomClosed: (data: { reason: 'completed' | 'opponent_left' | 'closed_by_owner' }) => void;
  addChatMessage: (msg: Omit<RPSRoomChatItem, 'id'>) => void;
  reset: () => void;
};

const initialState = {
  room_id: null as string | null,
  name: '',
  status: 'waiting' as RPSRoomStatus,
  role: 'spectator' as RPSRoomRole,
  myEmployeeId: null as string | null,
  maxGames: 3,
  timeLimitSec: 10,
  player1: null as RPSRoomPlayerInfo | null,
  player2: null as RPSRoomPlayerInfo | null,
  spectatorCount: 0,
  readyIds: [] as string[],
  matchStarted: false,
  winsNeeded: 2,

  gameNumber: 1,
  gameStartedAt: null as number | null,
  player1GamesWon: 0,
  player2GamesWon: 0,
  chosenIds: [] as string[],
  myChoiceSubmitted: false,
  lastReveal: null as RPSRoomReveal | null,
  revealHistory: [] as RPSRoomReveal[],

  matchEnd: null as RPSRoomMatchEnd | null,
  closeReason: null as RPSRoomState['closeReason'],

  chat: [] as RPSRoomChatItem[],
};

export const rpsRoomStore = create<RPSRoomState>((set, get) => ({
  ...initialState,

  setJoined: (data) =>
    set({
      room_id: data.room_id,
      name: data.name,
      status: data.status,
      role: data.role,
      myEmployeeId: data.my_employee_id,
      maxGames: data.max_games,
      timeLimitSec: data.time_limit_sec,
      player1: data.player1,
      player2: data.player2,
      readyIds: data.ready_ids,
      spectatorCount: data.spectator_count,
      matchStarted: data.status === 'playing',
    }),

  setStateUpdated: (data) =>
    set({ player1: data.player1, player2: data.player2, spectatorCount: data.spectator_count }),

  setReadyUpdated: (data) => set({ readyIds: data.ready_ids }),

  setMatchStarted: (data) =>
    set({
      status: 'playing',
      matchStarted: true,
      maxGames: data.max_games,
      timeLimitSec: data.time_limit_sec,
      winsNeeded: data.wins_needed,
      revealHistory: [],
    }),

  setGameStarted: (data) =>
    set({
      gameNumber: data.game_number,
      gameStartedAt: data.started_at,
      player1GamesWon: data.player1_games_won,
      player2GamesWon: data.player2_games_won,
      chosenIds: [],
      myChoiceSubmitted: false,
      lastReveal: null,
    }),

  setPlayerChosen: (data) => {
    const { chosenIds, myEmployeeId } = get();
    if (chosenIds.includes(data.employee_id)) return;
    set({
      chosenIds: [...chosenIds, data.employee_id],
      myChoiceSubmitted: data.employee_id === myEmployeeId ? true : get().myChoiceSubmitted,
    });
  },

  setGameRevealed: (data) =>
    set((s) => ({
      lastReveal: data,
      revealHistory: s.revealHistory.some((r) => r.game_number === data.game_number)
        ? s.revealHistory
        : [...s.revealHistory, data],
    })),

  setMatchEnded: (data) =>
    set({
      matchEnd: data,
      player1GamesWon: data.player1_games_won,
      player2GamesWon: data.player2_games_won,
      status: 'ended',
    }),

  setRoomClosed: (data) => set({ closeReason: data.reason }),

  addChatMessage: (msg) =>
    set((s) => ({
      chat: [...s.chat, { ...msg, id: `${msg.sent_at}_${msg.sender_id}_${s.chat.length}` }].slice(-200),
    })),

  reset: () => set({ ...initialState, chat: [] }),
}));
